import { history } from '@umijs/max';
import { useSession } from '@zhiyue/core';
import React from 'react';
import { readCurrentTenantUuid } from '@/adapters/tenantStorage';
import {
  clearOpenPlatformToken,
  fetchOpenPlatformToken,
  type OpenPlatformTokenResult,
} from '@/services/openPlatformToken';
import {
  clearMemberDirectory,
  loadMemberDirectory,
} from '@/state/memberDirectory';

export const OpenPlatformAccessTokenContext = React.createContext<string>('');

const publicPaths = ['/user/login', '/callback', '/user/access-denied'];
const normalizeText = (value: unknown) => String(value ?? '').trim();

export interface OpenPlatformBootstrapProps {
  children?: React.ReactNode;
}

export const OpenPlatformBootstrap: React.FC<OpenPlatformBootstrapProps> = ({
  children,
}) => {
  const { currentUser, loading } = useSession();
  const [tokenInfo, setTokenInfo] =
    React.useState<OpenPlatformTokenResult | null>(null);

  const organizationUuid = normalizeText(
    (currentUser as (typeof currentUser & { organizationUuid?: string }) | null)
      ?.organizationUuid || currentUser?.lastLoginOrganizationUuid,
  );
  const tenantUuid = normalizeText(
    currentUser?.tenantUuid || readCurrentTenantUuid() || organizationUuid,
  );
  const memberUuid = normalizeText(currentUser?.memberUuid);
  const userKey = [tenantUuid, organizationUuid, memberUuid].join('|');

  const bootstrapUser = React.useMemo(
    () =>
      currentUser && organizationUuid && memberUuid
        ? {
            tenantUuid: tenantUuid || organizationUuid,
            organizationUuid,
            memberUuid,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userKey],
  );

  const currentPath =
    (typeof window !== 'undefined' ? window.location?.pathname : '') ||
    history?.location?.pathname ||
    '';
  const isPublicPath = publicPaths.includes(currentPath);

  React.useEffect(() => {
    if (loading || isPublicPath) return;

    if (!bootstrapUser) {
      clearMemberDirectory();
      clearOpenPlatformToken();
      setTokenInfo(null);
      return;
    }

    let cancelled = false;

    fetchOpenPlatformToken(bootstrapUser)
      .then((result) => {
        if (!cancelled) {
          setTokenInfo(result);
        }
        return loadMemberDirectory(bootstrapUser);
      })
      .catch((err) => {
        if (!cancelled) {
          // 降级保护：记录警告但不阻断页面正常渲染
          console.warn('[OpenPlatformBootstrap] 租户成员目录或开放平台 Token 加载失败:', err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [bootstrapUser, isPublicPath, loading, userKey]);

  // 定时刷新 Token
  React.useEffect(() => {
    if (!bootstrapUser || !tokenInfo?.expiresAt) return;

    const refreshDelay = Math.max(tokenInfo.expiresAt - Date.now() - 60_000, 5_000);
    const timer = window.setTimeout(() => {
      clearOpenPlatformToken();
      void fetchOpenPlatformToken(bootstrapUser)
        .then(setTokenInfo)
        .catch(() => {
          setTokenInfo(null);
        });
    }, refreshDelay);

    return () => window.clearTimeout(timer);
  }, [bootstrapUser, tokenInfo]);

  return (
    <OpenPlatformAccessTokenContext.Provider value={tokenInfo?.accessToken || ''}>
      {children}
    </OpenPlatformAccessTokenContext.Provider>
  );
};

export default OpenPlatformBootstrap;
